/**
 * Campaign params capture for cross-redirect attribution.
 *
 * Some hosts (e.g. Skello) strip `location.search` shortly after page load via
 * `history.replaceState`. When Rose builds a CTA destination it rebuilds a clean
 * URL from config, so `utm_*` / `gclid` / etc. are dropped from the visitor's
 * conversion landing.
 *
 * This module snapshots allowlisted attribution params once per browser session
 * and keeps them in `sessionStorage` so the CTA URL builders can reattach them
 * at click time, even after the host has stripped the URL.
 *
 * - Capture is idempotent per parameter: the first non-empty value wins.
 *   Re-visits within the same session do not overwrite a prior capture (avoids
 *   losing the landing's params if the visitor later navigates to a clean
 *   internal page).
 * - Read returns `{}` when storage is unavailable.
 * - Stored values are URL-decoded strings (the values returned by `URLSearchParams.get`).
 */

// Use `inboundx_` prefix for sessionStorage keys (consistent with
// `inboundx_session_*`, `inboundx_chat_state_*`). Do not use a `rose_*` /
// radar-aligned prefix — the reveal SDK sweeps its namespace on cookie-consent
// grant via `migrate()`, moving every matching key into cookies and deleting
// the sessionStorage entry, which would wipe attribution mid-session.
import { getConsentMode } from '../../services/consentMode';
import { getSessionStorageIfAvailable } from './storageAvailability';

const STORAGE_KEY = 'inboundx_campaign_params_v1';

export const DEFAULT_CTA_PARAMETERS_FORWARDING_ALLOWLIST = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'utm_id',
    'gclid',
    'gclsrc',
    'gbraid',
    'wbraid',
    'gad_source',
    'gad_campaignid',
    'dclid',
    'fbclid',
    'msclkid',
    'li_fat_id',
    'ttclid',
    'twclid',
    'ScCid',
    'epik',
] as const;

export type CampaignParamKey = (typeof DEFAULT_CTA_PARAMETERS_FORWARDING_ALLOWLIST)[number];

function normalizeParamAllowlist(allowlist: readonly string[]): string[] {
    const normalized = new Set<string>();
    for (const key of allowlist) {
        const trimmed = key.trim();
        if (trimmed) {
            normalized.add(trimmed);
        }
    }
    return Array.from(normalized);
}

function readStoredAll(storage: Storage): Record<string, string> {
    try {
        const raw = storage.getItem(STORAGE_KEY);
        if (!raw) return {};
        const parsed: unknown = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return {};
        const out: Record<string, string> = {};
        for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
            if (key.trim() && typeof value === 'string' && value.length > 0) {
                out[key] = value;
            }
        }
        return out;
    } catch {
        return {};
    }
}

function pickAllowedParams(
    values: Record<string, string>,
    allowlist: readonly string[]
): Record<string, string> {
    const out: Record<string, string> = {};
    for (const key of normalizeParamAllowlist(allowlist)) {
        const value = values[key];
        if (value) {
            out[key] = value;
        }
    }
    return out;
}

function extractFromSearch(search: string, allowlist: readonly string[]): Record<string, string> {
    const params = new URLSearchParams(search);
    const out: Record<string, string> = {};
    for (const key of normalizeParamAllowlist(allowlist)) {
        const value = params.get(key);
        if (value && value.length > 0) {
            out[key] = value;
        }
    }
    return out;
}

/**
 * Capture allowlisted campaign params from the current `location.search` into
 * sessionStorage. No-op if storage unavailable, search empty, or a prior
 * non-empty capture already exists for the same parameter in this session.
 *
 * Returns the snapshot now stored (existing or newly written), `{}` if nothing
 * captured.
 */
export function captureCampaignParamsFromLocation(
    allowlist: readonly string[] = DEFAULT_CTA_PARAMETERS_FORWARDING_ALLOWLIST
): Record<string, string> {
    if (getConsentMode() !== 'full') return {};
    const allowedKeys = normalizeParamAllowlist(allowlist);
    if (allowedKeys.length === 0) return {};

    const storage = getSessionStorageIfAvailable();
    if (!storage) return {};

    const existing = readStoredAll(storage);
    const existingAllowed = pickAllowedParams(existing, allowedKeys);

    if (typeof window === 'undefined' || !window.location?.search) return existingAllowed;

    const captured = extractFromSearch(window.location.search, allowedKeys);
    if (Object.keys(captured).length === 0) return existingAllowed;

    const merged = { ...captured, ...existing };
    const addedNewParam = Object.keys(captured).some((key) => !(key in existing));

    if (addedNewParam) {
        try {
            storage.setItem(STORAGE_KEY, JSON.stringify(merged));
        } catch {
            // Quota / privacy mode: persistence failed; subsequent
            // getStoredCampaignParams() calls will return {} and CTA attribution
            // is lost for this session.
        }
    }

    return pickAllowedParams(merged, allowedKeys);
}

/**
 * Return the campaign params stored in sessionStorage, `{}` if none / storage
 * unavailable.
 */
export function getStoredCampaignParams(
    allowlist: readonly string[] = DEFAULT_CTA_PARAMETERS_FORWARDING_ALLOWLIST
): Record<string, string> {
    if (getConsentMode() !== 'full') return {};
    const allowedKeys = normalizeParamAllowlist(allowlist);
    if (allowedKeys.length === 0) return {};

    const storage = getSessionStorageIfAvailable();
    if (!storage) return {};
    return pickAllowedParams(readStoredAll(storage), allowedKeys);
}

/** Test-only helper. */
export function clearStoredCampaignParamsForTests(): void {
    const storage = getSessionStorageIfAvailable();
    if (!storage) return;
    try {
        storage.removeItem(STORAGE_KEY);
    } catch {
        // ignore
    }
}
